"use client";

import { rmcClient } from "@/lib/rmcClient";
import {
  formatTrialBalanceAmount,
  parseTrialBalanceResponse,
} from "@/lib/trialBalance";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

function ledgerLabel(type) {
  return type === "purchase" ? "Purchase" : "Sales";
}

const TrialBalancePrint = forwardRef(function TrialBalancePrint(_, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: `trial-balance-${snapshot?.meta?.ledger_type || "sales"}`,
  });

  useImperativeHandle(ref, () => ({
    openPrint: async ({ ledgerType, dateFrom, dateTo, accountName } = {}) => {
      const res = await rmcClient.getTrialBalance({
        ledger_type: ledgerType || "sales",
        date_from: dateFrom,
        date_to: dateTo,
        ...(accountName?.trim() ? { account_name: accountName.trim() } : {}),
      });
      const parsed = parseTrialBalanceResponse(res.data);
      flushSync(() => {
        setSnapshot({
          ...parsed,
          meta: {
            ...parsed.meta,
            ledger_type: ledgerType || "sales",
            date_from: dateFrom,
            date_to: dateTo,
          },
          printedAt: new Date().toLocaleString("en-IN"),
        });
      });
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  const companyName = company?.name || company?.company_name || "";
  const companyBlessing = (company?.blessing || company?.invoice_blessing || "").trim();
  const rows = snapshot?.rows ?? [];
  const totals = snapshot?.totals ?? {};
  const meta = snapshot?.meta ?? {};
  const typeLabel = ledgerLabel(meta.ledger_type);
  const period =
    meta.date_from && meta.date_to
      ? `${meta.date_from} to ${meta.date_to}`
      : meta.date_from || meta.date_to || "";

  const amt = (v) => formatTrialBalanceAmount(v);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white p-6 text-black print:block">
          <div className="mx-auto max-w-[297mm]">
            {companyBlessing ? (
              <p className="mb-2 text-center text-xs italic text-slate-700">{companyBlessing}</p>
            ) : null}
            {companyName ? (
              <p className="mb-1 text-center text-sm font-bold">{companyName}</p>
            ) : null}
            <h1 className="mb-1 text-center text-base font-bold">Trial Balance</h1>
            <p className="mb-1 text-center text-xs text-slate-700">{typeLabel} ledger</p>
            {period ? (
              <p className="mb-1 text-center text-xs text-slate-700">{period}</p>
            ) : null}
            <p className="mb-4 text-center text-[10px] text-slate-600">
              Printed {snapshot?.printedAt ?? ""} · {rows.length} account
              {rows.length === 1 ? "" : "s"}
            </p>

            <table className="w-full border-collapse border border-slate-800 text-[9px]">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-800 px-1.5 py-1 text-left font-semibold">
                    Account Name
                  </th>
                  <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">
                    Opening Dr
                  </th>
                  <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">
                    Opening Cr
                  </th>
                  <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">
                    Debit Amount
                  </th>
                  <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">
                    Credit Amount
                  </th>
                  <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">
                    Closing Dr
                  </th>
                  <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">
                    Closing Cr
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="border border-slate-800 px-2 py-2 text-slate-600">
                      No records
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.account_id ?? row.account_name}>
                      <td className="border border-slate-800 px-1.5 py-1">{row.account_name}</td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {amt(row.opening_debit)}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {amt(row.opening_credit)}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {amt(row.debit_amount)}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {amt(row.credit_amount)}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {amt(row.closing_debit)}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {amt(row.closing_credit)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-100 font-semibold">
                    <td className="border border-slate-800 px-1.5 py-1.5 text-right">Totals</td>
                    <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                      {amt(totals.opening_debit)}
                    </td>
                    <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                      {amt(totals.opening_credit)}
                    </td>
                    <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                      {amt(totals.debit_amount)}
                    </td>
                    <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                      {amt(totals.credit_amount)}
                    </td>
                    <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                      {amt(totals.closing_debit)}
                    </td>
                    <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                      {amt(totals.closing_credit)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
    </div>
  );
});

TrialBalancePrint.displayName = "TrialBalancePrint";

export default TrialBalancePrint;
